import { format, fromUnixTime, getUnixTime, differenceInDays, differenceInMonths } from 'date-fns';
import { formatPrescriptionDosage } from '@/utils/prescriptionDosage';
import IconRefresh from '@/components/icon/IconRefresh.vue';
export default {
  components: {
    IconRefresh
  },
  props: {
    disableAirtable: {
      type: Boolean,
      default: false
    },
    selected: {
      type: Boolean,
      default: false
    },
    enableControls: {
      type: Boolean,
      default: false
    },
    prescription: {
      type: Object,
      required: true
    },
    timer: {
      type: Number,
      default: 0,
      required: false
    },
    ongoingItems: {
      type: Array,
      default() {
        return [];
      }
    },
    // Not the same as slideout patient data
    /*
      {
        firstname: 'John',
        lastname: 'Doe',
        hysterectomy: false,
      }
    */
    patientData: {
      type: Object,
      default() {
        return {
          firstname: '',
          lastname: '',
          hysterectomy: false
        };
      }
    }
  },
  data() {
    return {
      statusMapper: {
        DRAFTED: {
          text: 'Draft',
          controls: {
            play: false,
            pause: false,
            stop: true
          }
        },
        PROCESSING: {
          text: 'Processing',
          controls: {
            play: false,
            pause: false,
            stop: false
          }
        },
        PRESCRIBED: {
          text: 'Prescribed',
          controls: {
            play: false,
            pause: true,
            stop: true
          }
        },
        ORDERED: {
          text: 'Ordered',
          controls: {
            play: false,
            pause: true,
            stop: true
          }
        },
        SCHEDULED: {
          text: 'Scheduled',
          controls: {
            play: true,
            pause: false,
            stop: true
          }
        },
        FAILED: {
          text: 'Failed',
          controls: {
            play: true,
            pause: false,
            stop: true
          }
        },
        PAUSED: {
          text: 'Paused',
          controls: {
            play: true,
            pause: false,
            stop: true
          }
        },
        COMPLETED: {
          text: 'Completed',
          controls: {
            play: false,
            pause: false,
            stop: false
          }
        },
        WAITING: {
          text: 'Waiting',
          controls: {
            play: false,
            pause: true,
            stop: true
          }
        },
        CANCELLED: {
          text: 'Cancelled',
          controls: {
            play: false,
            pause: false,
            stop: false
          }
        },
        REFILLED: {
          text: 'Refilled',
          controls: {
            play: false,
            pause: false,
            stop: true
          }
        }
      }
    };
  },
  computed: {
    // Existing Sildenafil Arousal Cream prescriptions stored the legacy dosage
    // ("1mg/ml"); show the corrected strength on the card. Newer prescriptions
    // already store "4% (40mg/ml)", so this is a no-op for them.
    displayDosage() {
      return formatPrescriptionDosage(this.prescription.medicinename, this.prescription.dosage);
    },
    hasRefills() {
      const currentRefills = this.prescription.refills - this.prescription.paidrefills;
      return currentRefills !== 0;
    },
    shouldTreatAsCompleted() {
      // Stripe-cancelled prescriptions must never be re-labeled as PRESCRIBED,
      // even if stale backend fields still say PROCESSING (see effectiveStatus).
      if (this.isStripeCancellable) {
        return false;
      }
      const isOlderThan3Months = this.prescription.timestampprescibed ? differenceInMonths(new Date(), fromUnixTime(this.prescription.timestampprescibed)) >= 3 : false;
      return (this.prescription.prescriptionstatus === 'PRESCRIBED' || this.prescription.prescriptionstatus === 'PROCESSING') && !this.prescription.airtablestatus;
    },
    isAirtableProcessing() {
      return !!this.prescription.airtablestatus && this.prescription.airtablestatus === 'Processing';
    },
    // True when the prescription was cancelled by Stripe and is eligible to be
    // restarted. Uses `=== true` (not `Object.hasOwn`) so a stale
    // `can_restart: false` from an inconsistent backend response is not treated
    // as active. When true, we force the card into a consistent CANCELLED state
    // regardless of leftover processing/delay fields.
    isStripeCancellable() {
      return this.prescription.can_restart === true;
    },
    effectiveStatus() {
      // Prioritize can_restart === true over any stale prescriptionstatus so a
      // Stripe-cancelled prescription always renders as CANCELLED, even when
      // the backend has not yet cleared PROCESSING/WAITING/etc.
      if (this.isStripeCancellable) {
        return 'CANCELLED';
      }
      if (this.shouldTreatAsCompleted) {
        return 'PRESCRIBED';
      }
      return this.prescription.prescriptionstatus;
    }
  },
  methods: {
    getRefillString(med) {
      if (med.refills) {
        if (['SCHEDULED', 'WAITING', 'PROCESSING', 'MIGRATED', 'DRAFTED', 'FAILED'].includes(med.prescriptionstatus)) {
          return `Refills: ${med.refillinput}`;
        }
        const refills = med.refills - med.paidrefills;
        if (med.paidrefills === 0 && (med.paymentstatus === 'FAILED' || med.prescriptionstatus === 'PAUSED' || med.prescriptionstatus === 'CANCELLED')) {
          return `${refills - 1} out of ${med.refills - 1} refills`;
        }
        if (med.refills && med.paidrefills) {
          return `${refills} out of ${med.refills - 1} refills`;
        } else {
          return 'One time purchase';
        }
      } else {
        return 'No refills';
      }
    },
    parseUnixDate(date, formatString) {
      return format(fromUnixTime(date), formatString);
    },
    openAirtableRecord(prescription) {
      if (!this.disableAirtable) {
        this.$store.dispatch('infoview/openAirtableDisplay', prescription);
      }
    },
    // Button disablers
    disablePlay() {
      var _this$statusMapper$th;
      // Explicit `=== true` check: a stale `can_restart: false` from an
      // inconsistent backend response should not override the status map.
      if (this.prescription.can_restart === true) {
        return false;
      }
      return !((_this$statusMapper$th = this.statusMapper[this.effectiveStatus]) !== null && _this$statusMapper$th !== void 0 && _this$statusMapper$th.controls.play);
    },
    disablePause() {
      var _this$statusMapper$th2;
      return !((_this$statusMapper$th2 = this.statusMapper[this.effectiveStatus]) !== null && _this$statusMapper$th2 !== void 0 && _this$statusMapper$th2.controls.pause);
    },
    disableStop() {
      var _this$statusMapper$th3;
      return !((_this$statusMapper$th3 = this.statusMapper[this.effectiveStatus]) !== null && _this$statusMapper$th3 !== void 0 && _this$statusMapper$th3.controls.stop);
    },
    isMoreThan90Days(date) {
      if (!date) {
        return false;
      }
      return differenceInDays(new Date(), new Date(date * 1000)) > 90;
    },
    openPausePrescription(prescription) {
      if (this.effectiveStatus == 'WAITING') {
        this.$emit('open-stop-delay', prescription);
      } else {
        this.$emit('open-pause-prescription', prescription);
      }
    },
    async openSingleCancel(prescription) {
      if (!this.statusMapper[this.effectiveStatus].controls.stop) {
        return;
      } else if (this.effectiveStatus == 'WAITING') {
        this.$emit('pause-timer', prescription);
      } else {
        this.$emit('open-cancel-single', prescription);
      }
    },
    getUnixTime
  }
};